import JsonLd from 'components/common/JsonLd';
import { BEST_RATING, DEFAULT_BRAND_REVIEWER_NAME, WORST_RATING } from 'constants/jsonld';
import { constructMSAImageUrl } from 'helpers/index';
import { getReviewDatePublished } from 'helpers/jsonld';
import marketplace from 'cfg/marketplace.json';

const {
  search: { msaMelodyImageParams }
} = marketplace;

// The brand-review slot shape is not centrally typed (SlotDetails.reviews is untyped); this captures
// just the fields the JSON-LD consumes.
interface BrandReview {
  productName: string;
  overallRating: number | string;
  summary?: string;
  name?: string;
  reviewDate?: number | string;
  imageId?: string;
  link?: string;
}

interface Props {
  reviews?: BrandReview[];
  brandName?: string;
}

/**
 * Emits JSON-LD (Google's preferred format) for the brand page's "Latest Reviews" block, replacing
 * the microdata SingleReview previously emitted inline.
 *
 * Shape rationale:
 * - Product { review }, NOT Review { itemReviewed: Product }. Google validates any Product it
 *   extracts, so a bare Product with only name/image/url fails "Either offers, review, or
 *   aggregateRating should be specified". Nesting the review under the Product satisfies that.
 * - One Product per unique reviewed product, carrying a single representative review. The brand page
 *   shows only a small sample of each product's reviews and the payload has no true per-product
 *   totals, so we can't emit an honest aggregateRating. Keeping a single review per Product avoids
 *   both the "Multiple reviews without aggregateRating" error and misrepresenting review counts.
 */
export default function BrandReviewsJsonLd({ reviews, brandName }: Props) {
  if (!reviews || reviews.length === 0) {
    return null;
  }

  // The list is "Latest Reviews", so the first occurrence of each product is its most recent review.
  // Dedupe to one representative review per product (keyed by link, falling back to product name).
  const representativeByProduct = new Map<string, BrandReview>();
  reviews.forEach(review => {
    const key = review.link || review.productName;
    if (key && !representativeByProduct.has(key)) {
      representativeByProduct.set(key, review);
    }
  });

  const productGraph = [...representativeByProduct.values()].map(({ productName, overallRating, summary, name, reviewDate, imageId, link }) => {
    const imageSrc = imageId ? constructMSAImageUrl(imageId, msaMelodyImageParams) : undefined;
    const datePublished = getReviewDatePublished(reviewDate);
    return {
      '@type': 'Product',
      'name': productName,
      ...(brandName && { brand: { '@type': 'Brand', 'name': brandName } }),
      ...(imageSrc && { image: imageSrc }),
      ...(link && { url: link }),
      'review': [
        {
          '@type': 'Review',
          'author': { '@type': 'Person', 'name': name || DEFAULT_BRAND_REVIEWER_NAME },
          'reviewRating': {
            '@type': 'Rating',
            'ratingValue': String(overallRating),
            'bestRating': BEST_RATING,
            'worstRating': WORST_RATING
          },
          ...(summary && { reviewBody: summary }),
          ...(datePublished && { datePublished })
        }
      ]
    };
  });

  // @context is supplied by JsonLd; @graph lets one script carry the several reviewed products.
  return <JsonLd data={{ '@graph': productGraph }} />;
}
